/**
 * @module engine3d/levels
 * @summary Plant floors: the hand-written level set, the level CRUD the 3D HUD
 *   drives, and the automatic assignment behind the Arrange command.
 * @description
 *   How many floors a building has, what they are called and how high they sit
 *   is a fact about the BUILDING. It is not derivable from a flow sheet, and
 *   guessing it produces floors nobody asked for — so a drawing opens flat on
 *   a single ground floor and every floor above it is written by hand in the
 *   Levels panel. `ensureLevels` is what enforces that.
 *
 *   A compounding line is still gravity-fed — the silo discharges into the
 *   feeder, the feeder into the extruder throat — so the automatic lifting is
 *   kept for anyone who wants it, behind the Arrange command. It has to be
 *   ASKED FOR, because it rewrites every floor and moves every item.
 *
 *   `autoAssign` does that lifting. It combines two sources of truth:
 *
 *     1. **What the equipment is.** A silo belongs high up; an extruder belongs
 *        on the ground. That is the `TIER_PRIOR` table.
 *     2. **How it is connected.** If A discharges from its bottom port into B's
 *        top port, A must end up above B whatever the table says. Those are
 *        hard constraints, relaxed until every one of them holds.
 *
 *   The result is a tier per item; floors are then CREATED to match, as many as
 *   the drawing turns out to need, and each floor is spaced by the tallest
 *   thing standing on the one below plus headroom. That is what makes the
 *   arrangement look deliberate rather than evenly stacked.
 *
 *   // INVARIANT: levels are 3D-only. Nothing here is read by the 2D renderer.
 */
import type { Connection, Level, Shape } from '../types';
import { S, emit } from '../engine/state';
import { getModelFor } from './models';

/** Headroom between the tallest item on a floor and the deck above it. */
const HEADROOM = 22;
/** Never build more floors than this, however tangled the drawing. */
const MAX_TIERS = 6;

/**
 * Which floor a symbol belongs on when nothing else says otherwise.
 * 2 = storage deck, 1 = feed mezzanine, 0 = ground.
 */
const TIER_PRIOR: Record<string, number> = {
  // Storage that discharges downward — the top of a gravity train.
  silo: 2, powdersilo: 2, granulessilo: 2, rawmatsilo: 2, fgsilo: 2,
  bulkbag: 2, ibc: 2, hoistdebag: 2, bagdump: 2, manualdebag: 2,
  debagging: 2, debaghopper: 2, supermixer: 2,

  // Feeding, dosing and separation — the middle of the train.
  hopper: 1, liwfeeder: 1, volfeeder: 1, scsfeeder: 1, tcsfeeder: 1,
  sidefeeder: 1, forcedfeeder: 1, liquidfeeder: 1, rotaryvalve: 1,
  weighbatcher: 1, vacloader: 1, vacreceiver: 1, filterreceiver: 1,
  cyclone: 1, buffertank: 1, drytank: 1, coldmixer: 1, screwconv: 1,
  divertervalve: 1, diverter2: 1, diverter3: 1, vibroscreen: 1,
  classifier: 1, dedusting: 1, centdryer: 1, centdewater: 1,
  pelletdryer: 1, fluidizedbed: 1, connectingtube: 1, elbow90: 1,

  // Everything else defaults to 0 — extruders, dies, downstream, utilities,
  // valves, instruments and annotations all live on the ground.
};

/**
 * Floors are named the way people on site name them. The ground is the ground;
 * everything above it is a numbered floor, because "which floor is that on?" is
 * the question these labels exist to answer.
 */
const levelName = (i: number): string => (i === 0 ? 'Ground Floor' : `Floor ${i}`);

/**
 * True when this connection implies gravity flow — the source must sit above
 * the destination.
 *
 * Only solid lines count. A dashed cooling-water line runs wherever it is
 * convenient and says nothing about which floor anything is on; treating it as
 * a constraint used to push chillers onto the roof.
 */
function isGravity(cn: Connection): boolean {
  if (cn.lineStyle && cn.lineStyle !== 'solid') return false;
  // Leaving from a TOP port is material being lifted or blown out — a cyclone's
  // clean-air outlet, a vacuum take-off — so it says nothing about which end is
  // higher, whatever port it arrives at. Without this exclusion a conveying
  // line reads as a gravity drop and pushes the source onto a deck above its
  // own destination.
  if (cn.fromPort === 'N') return false;
  return cn.fromPort === 'S' || cn.toPort === 'N';
}

/** The tier each shape should sit on, honouring priors and gravity. */
function solveTiers(shapes: Shape[], connections: Connection[]): Map<string, number> {
  const tier = new Map<string, number>();
  for (const s of shapes) tier.set(s.id, TIER_PRIOR[s.type] ?? 0);

  const links = connections.filter(isGravity);
  // Relaxation: raise the upstream item until it clears its downstream one.
  // Converges in one pass per chain link; the cap only matters if the drawing
  // contains a loop, in which case it stops rather than climbing forever.
  for (let iter = 0; iter < MAX_TIERS * 2; iter++) {
    let changed = false;
    for (const cn of links) {
      const a = tier.get(cn.fromId);
      const b = tier.get(cn.toId);
      if (a === undefined || b === undefined) continue;
      if (a <= b && b + 1 <= MAX_TIERS) {
        tier.set(cn.fromId, b + 1);
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Compact the tiers actually used, so a drawing that happens to skip tier 1
  // doesn't get an empty floor built for it.
  const used = [...new Set(tier.values())].sort((a, b) => a - b);
  const remap = new Map(used.map((t, i) => [t, i]));
  for (const [id, t] of tier) tier.set(id, remap.get(t) ?? 0);
  return tier;
}

/** Tallest model standing on a given tier, used to space the floor above it. */
function tallestOn(shapes: Shape[], tier: Map<string, number>, t: number): number {
  let max = 0;
  for (const s of shapes) {
    if ((tier.get(s.id) ?? 0) !== t) continue;
    if (s.type === 'zonebox' || s.type === 'textlabel' || s.type === 'processarrow') continue;
    const h = getModelFor(s).height;
    if (h > max) max = h;
  }
  return max;
}

/**
 * Derive floors for the current document and assign every shape to one.
 *
 * Only ever runs when asked for — the Arrange command in the 3D HUD. It
 * REPLACES the floor set, so any floors written by hand are lost; that is why
 * it is a command with an undo step behind it and not something that happens
 * on the way into the view.
 *
 * @param force  Re-derive even for shapes that already carry a level.
 */
export function autoAssign(force = false): void {
  const shapes = S.shapes;
  if (!shapes.length) {
    if (!S.levels.length) S.levels = [{ id: 'L0', name: levelName(0), elevation: 0 }];
    return;
  }
  const tier = solveTiers(shapes, S.connections);
  const tierCount = Math.max(1, Math.max(...tier.values()) + 1);

  // Build the floors, spacing each by the tallest item on the one below.
  const levels: Level[] = [];
  let y = 0;
  for (let t = 0; t < tierCount; t++) {
    levels.push({ id: 'L' + t, name: levelName(t), elevation: Math.round(y) });
    y += tallestOn(shapes, tier, t) + HEADROOM;
  }
  S.levels = levels;

  for (const s of shapes) {
    if (!force && s.level && levels.some((l) => l.id === s.level)) continue;
    // Annotations are floor markings; they belong on the ground whatever the
    // things drawn inside them are doing.
    const t = s.type === 'zonebox' || s.type === 'textlabel' || s.type === 'processarrow' ? 0 : (tier.get(s.id) ?? 0);
    s.level = levels[Math.min(t, levels.length - 1)].id;
    if (force) delete s.dz;
  }
}

/**
 * Make sure the document has somewhere to stand: one ground floor, at zero.
 *
 * Deliberately NOT `autoAssign`. A drawing opened in 3D should show the drawing
 * — flat on the ground, which is where a flow sheet actually puts everything —
 * and the storeys above it are the engineer's to write. Shapes are left
 * untouched: one with no floor of its own falls through `levelOf` onto the
 * lowest, so nothing has to be stamped onto the document to stand on the
 * ground.
 */
export function ensureLevels(): void {
  if (!S.levels.length) S.levels = [{ id: 'L0', name: levelName(0), elevation: 0 }];
}

// ─── Lookups ─────────────────────────────────────────────────────────────

export function getLevels(): Level[] {
  return S.levels;
}

/** The level a shape stands on, falling back to the lowest floor. */
export function levelOf(s: Shape): Level | undefined {
  return S.levels.find((l) => l.id === s.level) ?? S.levels[0];
}

/** Floor elevation + the item's own offset above it. */
export function baseYOf(s: Shape): number {
  return (levelOf(s)?.elevation ?? 0) + (s.dz || 0);
}

/** The nearest floor to an absolute height — used to snap a dragged item. */
export function nearestLevel(y: number): Level | undefined {
  let best: Level | undefined;
  let bd = Infinity;
  for (const l of S.levels) {
    const d = Math.abs(l.elevation - y);
    if (d < bd) {
      bd = d;
      best = l;
    }
  }
  return best;
}

// ─── Editing ─────────────────────────────────────────────────────────────

/** Keep floors ordered bottom-to-top; the HUD and the snap logic both rely on it. */
function sortLevels(): void {
  S.levels.sort((a, b) => a.elevation - b.elevation);
}

export function addLevel(): Level {
  const top = S.levels[S.levels.length - 1];
  const l: Level = {
    id: 'L' + Date.now().toString(36),
    name: levelName(S.levels.length),
    elevation: Math.round((top?.elevation ?? 0) + 110),
  };
  S.levels.push(l);
  sortLevels();
  emit('doc');
  return l;
}

/**
 * Delete a floor, moving anything standing on it down to the one below.
 * Refuses to remove the last floor — equipment has to stand on something.
 */
export function removeLevel(id: string): boolean {
  if (S.levels.length <= 1) return false;
  const i = S.levels.findIndex((l) => l.id === id);
  if (i < 0) return false;
  const fallback = S.levels[i === 0 ? 1 : i - 1];
  S.levels.splice(i, 1);
  for (const s of S.shapes) if (s.level === id) s.level = fallback.id;
  emit('doc');
  return true;
}

export function renameLevel(id: string, name: string): void {
  const l = S.levels.find((x) => x.id === id);
  if (!l) return;
  l.name = name;
  emit('doc');
}

export function setLevelElevation(id: string, elevation: number): void {
  const l = S.levels.find((x) => x.id === id);
  if (!l) return;
  l.elevation = Math.round(elevation);
  sortLevels();
  emit('doc');
}

/** Move every selected shape onto a floor, clearing any manual offset. */
export function assignSelectionTo(levelId: string, ids: string[]): void {
  for (const id of ids) {
    const s = S.shapes.find((x) => x.id === id);
    if (!s) continue;
    s.level = levelId;
    delete s.dz;
  }
  emit('doc');
}
